import { FiMenu } from 'react-icons/fi';
import { BottomNav } from '@/shared/layout/bottom-nav';
import { Sidebar } from '@/shared/layout/sidebar';
import { TopBar } from '@/shared/layout/top-bar';
import {
  topBarIconButtonClasses,
  topBarIconClasses,
} from '@/shared/layout/top-bar/styles';
import { cn } from '@/shared/lib';
import {
  shellClasses,
  shellContentClasses,
  shellMainClasses,
  shellMainWithBottomNavClasses,
} from '@/layouts/app-shell/styles';
import { TopBarIdentity } from '@/layouts/app-shell/components/top-bar-identity';
import { ProfileButton } from '@/layouts/app-shell/components/profile-button';
import { PendingAgreementGate } from '@/modules/agreements';
import { AssistantPanel } from '@/modules/assistant';
import type { AppShellProps } from '@/layouts/app-shell/types';
import { useAppShellProps } from '@/layouts/app-shell/hooks';

export function AppShell({ children, hideAssistant = false }: AppShellProps) {
  const {
    sidebarProps,
    bottomNavProps,
    sidebarToggleProps,
    identity,
    accountPanelJumpTo,
    accountPanelGroups,
    onSignOut,
  } = useAppShellProps();

  // The nav is driven by the registry, and a deployment may flag no destination
  // `mobile`. An empty nav is not a nav: rendering it leaves a 1px sliver, and
  // reserving its clearance leaves a band of dead canvas under every page.
  const hasBottomNav = bottomNavProps.items.length > 0;

  return (
    <div className={shellClasses}>
      <Sidebar {...sidebarProps} />
      <div className={shellContentClasses}>
        <TopBar
          navigationSlot={renderNavSlot(sidebarToggleProps)}
          actionsSlot={hideAssistant ? null : <AssistantPanel />}
          profileSlot={
            <>
              <TopBarIdentity identity={identity} />
              <ProfileButton
                identity={identity}
                onSignOut={onSignOut}
                jumpTo={accountPanelJumpTo}
                groups={accountPanelGroups}
              />
            </>
          }
        />
        <main className={cn(shellMainClasses, hasBottomNav && shellMainWithBottomNavClasses)}>
          <PendingAgreementGate />
          {children}
        </main>
      </div>
      {hasBottomNav ? <BottomNav {...bottomNavProps} /> : null}
    </div>
  );
}

function renderNavSlot(
  sidebarToggleProps: { onClick: () => void; 'aria-label': string; 'aria-expanded': boolean },
) {
  return (
    <button
      type="button"
      {...sidebarToggleProps}
      className={topBarIconButtonClasses}
      title="Navigation"
    >
      <FiMenu className={topBarIconClasses} aria-hidden="true" />
    </button>
  );
}

export type { AppShellProps } from '@/layouts/app-shell/types';